// Per-person preferences (Parity P1 wave 1; hybrid design §3.1 "rail state is a per-person
// preference", §3.1 "per-person collapse in `me/prefs`", files "per-person prefs"). One row per
// (company, user, key) in `user_pref`; a key exists only when it is whitelisted here, and its
// value is checked against the key's schema. Packages add their keys with ONE line each in
// `PREF_KEYS` (packages doc "Shared rules": "Prefs through `me/prefs` keys (P1 whitelist, one
// line per package)"). A key never read answers `value: null` (the client's default applies).
import { z } from "zod";
import { ViewKind } from "../enums";
import { boundedRecord, Timestamp, Uuid } from "../primitives";
import { Mood, VoiceId } from "../voice-catalogue";
import { CompanyParams } from "./common";
import { defineRoute } from "./route";

const base = "/v1/companies/{co}";
const core = { feature: "core" } as const;

/** P1: the task drawer's rail and the sections a person folded (section ids of the layout). */
export const TaskDrawerPref = z
  .strictObject({
    rail: z.enum(["open", "closed"]).optional(),
    collapsed: z.array(z.string().min(1).max(80)).max(200).optional(),
    /** TASK-DRAWER-CHAT: the conversation's share of the host's width (the drag handle). */
    rail_share: z.number().min(0.3).max(0.7).optional(),
  })
  .meta({ id: "TaskDrawerPref" });

/** P1: how the Files block lists (grid or list) and sorts. */
export const TaskFilesPref = z
  .strictObject({
    view: z.enum(["grid", "list"]).optional(),
    sort: z.enum(["name", "date", "size", "type"]).optional(),
  })
  .meta({ id: "TaskFilesPref" });

/**
 * WORKFORCE-BOARDVIEW: the view kind tab each board opens on for this person (board id → kind);
 * it wins over the board's `settings.default_kind`. A board left out opens on the board's default.
 */
export const BoardViewPref = z
  .strictObject({
    kinds: boundedRecord(ViewKind, 500, Uuid).optional(),
  })
  .meta({ id: "BoardViewPref" });

/** HOME-DASH: a Home widget's width in the 12-column grid (xs 2, s 3, m 4, l 6, xl 8, full 12). */
export const HomeWidgetSize = z.enum(["xs", "s", "m", "l", "xl", "full"]);

/**
 * HOME-DASH (Andrei, 2026-10-27: "people can reorganize things, like dashboards"): the person's
 * Home layout — widgets in order, each shown or hidden, with its width. A widget the client does
 * not know is ignored; a known widget left out takes its default place. No `widgets`: the default
 * layout ("Reset to default" writes `{}`).
 */
export const HomeLayoutPref = z
  .strictObject({
    widgets: z
      .array(
        z.strictObject({
          id: z
            .string()
            .regex(/^[a-z][a-z0-9_]{0,39}$/)
            .describe("The widget (`kpi_score`, `agent_runtime`, …)"),
          hidden: z.boolean().optional(),
          size: HomeWidgetSize.optional(),
        }),
      )
      .max(60)
      .refine((ws) => new Set(ws.map((w) => w.id)).size === ws.length, "a widget is listed twice")
      .optional(),
  })
  .meta({ id: "HomeLayoutPref" });

/**
 * VOICE-FRONT: the person's voice settings. `recall_transcript`: their voice sessions' transcript
 * may go to their Recall memory (default off; the transcript always goes to the session thread).
 */
export const VoiceFrontPref = z
  .strictObject({
    recall_transcript: z.boolean().optional(),
    /**
     * VOICE-CHOICES: the stock voice the person talks to (a catalogue id). Absent: the company
     * default, else the first free voice. A voice that is not available now (a premium voice
     * without its keys) falls back the same way.
     */
    voice_id: VoiceId.optional(),
    /** VOICE-CHOICES: the person's mood (speaking style); absent: Plain (no instructions). */
    mood: Mood.optional(),
  })
  .meta({ id: "VoiceFrontPref" });

const BriefTask = z.object({
  id: Uuid,
  number: z.int(),
  title: z.string(),
  status: z.string(),
  due: z.string().nullable().optional(),
});

/** A18 BRIEF-DATA: what the morning brief counted (services/api domain/briefs/morning.ts). */
export const MorningBriefFacts = z
  .object({
    zone: z.string(),
    since: Timestamp,
    until: Timestamp,
    overnight: z.object({
      runsCompleted: z.int(),
      runsFailed: z.int(),
      runsByAgent: z.array(z.object({ name: z.string(), n: z.int() })),
      cardsMoved: z.int(),
      moved: z.array(BriefTask),
      blockers: z.array(BriefTask.extend({ why: z.string() })),
      approvalsWaiting: z.int(),
    }),
    pipeline: z.object({
      agent: z.string().nullable(),
      found: z.boolean(),
      open: z.int(),
      newOvernight: z.int(),
      byStatus: z.array(z.object({ status: z.string(), n: z.int() })),
    }),
    today: z.object({
      date: z.string(),
      dueCount: z.int(),
      due: z.array(BriefTask),
      overdue: z.int(),
      events: z.array(z.object({ title: z.string(), at: z.string(), allDay: z.boolean() })),
    }),
  })
  .meta({ id: "MorningBriefFacts" });

export const MorningBriefQuery = z.object({
  since: Timestamp.optional().describe(
    "Start of the overnight window (default 18:00 the day before)",
  ),
  pipeline_agent: z.string().min(1).max(120).optional().describe("Default: Leads Bro"),
  style: z.enum(["spoken", "markdown"]).optional(),
});

export const MorningBrief = z
  .object({
    /** The company flag `voice_front`; off: `brief` is null. */
    voice_front: z.boolean(),
    brief: z.object({ text: z.string(), words: z.int(), facts: MorningBriefFacts }).nullable(),
  })
  .meta({ id: "MorningBrief" });
export type MorningBrief = z.infer<typeof MorningBrief>;

/**
 * The whitelist: key → the schema of its value. ONE line per package; never remove a key a
 * client may still write (a stored value of a removed key is simply never served).
 */
export const PREF_KEYS = {
  // P1
  task_drawer: TaskDrawerPref,
  task_files: TaskFilesPref,
  // WORKFORCE-BOARDVIEW
  board_view: BoardViewPref,
  // HOME-DASH
  home_layout: HomeLayoutPref,
  // VOICE-FRONT
  voice_front: VoiceFrontPref,
} as const satisfies Record<string, z.ZodType>;

export type PrefKey = keyof typeof PREF_KEYS;
export const PrefKeyName = z
  .string()
  .max(64)
  .regex(/^[a-z][a-z0-9_]{0,63}$/)
  .describe("A whitelisted preference key (`task_drawer`, `task_files`, …)");

export const PrefParams = z.object({ co: Uuid, key: PrefKeyName });

export const PrefPut = z
  .strictObject({ value: z.unknown().describe("Checked against the key's schema") })
  .meta({ id: "PrefPut" });

export const Pref = z
  .object({
    key: z.string(),
    value: z.unknown().nullable().describe("null: never set (the client's default applies)"),
    updated_at: Timestamp.nullable(),
  })
  .meta({ id: "Pref" });
export type Pref = z.infer<typeof Pref>;

/** Parses a value for `key`; null when the key is not whitelisted. */
export function prefSchema(key: string): z.ZodType | null {
  return Object.hasOwn(PREF_KEYS, key) ? PREF_KEYS[key as PrefKey] : null;
}

export const meRoutes = {
  "me.prefs.get": defineRoute({
    ...core,
    id: "me.prefs.get",
    method: "get",
    path: `${base}/me/prefs/{key}`,
    tag: "me",
    summary: "One of the caller's preferences in this company (null when never set)",
    access: "member",
    params: PrefParams,
    response: { status: 200, schema: Pref },
    errors: [],
    emits: [],
  }),
  "me.prefs.put": defineRoute({
    ...core,
    id: "me.prefs.put",
    method: "patch",
    path: `${base}/me/prefs/{key}`,
    tag: "me",
    summary: "Set one of the caller's preferences (the whole value of a whitelisted key)",
    access: "member",
    params: PrefParams,
    body: PrefPut,
    response: { status: 200, schema: Pref },
    errors: [],
    emits: ["pref.changed"],
  }),
  "me.morningBrief": defineRoute({
    ...core,
    id: "me.morningBrief",
    method: "get",
    path: `${base}/me/morning-brief`,
    tag: "me",
    summary: "The caller's morning brief: overnight, pipeline, today (voice front, VOICE-FRONT)",
    description:
      "Read under the caller's task visibility; under 300 words. Off unless the company flag " +
      "`voice_front` is on (then `brief` is null).",
    access: "member",
    params: CompanyParams,
    query: MorningBriefQuery,
    response: { status: 200, schema: MorningBrief },
    errors: [],
    emits: [],
  }),
} as const;
